import React, { useState } from 'react'
import moment from 'moment'
import {
  DateInput,
  IChannelsV1List,
  useArray,
  useFormSubmit,
  useModal,
  varClass,
} from '../../shared'
import { MultiSelectDropdown } from './shared'
import { Controller, useForm } from 'react-hook-form'
import { postRequest, putRequest } from '../../../services'

export const BoostModal = (props) => {
  const { advertiserUuid, boosts, channels, departments, onDone } = props

  const { hideModal } = useModal()

  const isNew = !boosts?.length
  const isAllChannels =
    isNew || boosts?.boostChannelUuids?.length === channels?.length
  const isFlat = !isNew && (boosts?.boostFromFlat || boosts?.boostToFlat)

  const [selectedChannelsMethod, setSelectedChannelsMethod] = useState<
    'all' | 'specific'
  >(isAllChannels ? 'all' : 'specific')
  const {
    values: selectedChannels,
    toggle: toggleSelectedChannel,
    clear: clearSelectedChannels,
  } = useArray(
    isAllChannels
      ? channels.map((channel: IChannelsV1List) => channel.channelUuid)
      : boosts?.boostChannelUuids || []
  )

  const form = useForm({
    defaultValues: {
      name: boosts?.boostName || '',
      department: boosts?.boostDepartment || departments[0].key,
      commissionType: isFlat ? 'flat' : 'percentage',
      fromValue: boosts?.boostFromPercentage || boosts?.boostFromFlat || 0,
      toValue: boosts?.boostToPercentage || boosts?.boostToFlat || 0,
      startDate: boosts ? new Date(boosts.boostStartDate) : '',
      endDate: boosts ? new Date(boosts.boostEndDate) : '',
    },
  })
  const { register, handleSubmit, control } = form
  const [onSubmit, submitting] = useFormSubmit(async (values) => {
    const channelUuids: string[] = []
    if (selectedChannelsMethod === 'all') {
      channelUuids.push(...channels.map((channel) => channel.channelUuid))
    } else if (selectedChannelsMethod === 'specific') {
      channelUuids.push(...(selectedChannels as string[]))
    }

    let fromPercentage = 0
    let toPercentage = 0
    let fromFlat = 0
    let toFlat = 0
    if (values.commissionType === 'percentage') {
      fromPercentage = values.fromValue
      toPercentage = values.toValue
    } else {
      fromFlat = values.fromValue
      toFlat = values.toValue
    }

    const startDate = moment(values.startDate || new Date()).format(
      'YYYY-MM-DD'
    )
    const endDate = moment(values.endDate || new Date()).format('YYYY-MM-DD')

    if (isNew) {
      await postRequest(`/advs/v1/boosts/${advertiserUuid}`, {
        channelUuids,
        name: values.name,
        department: values.department,
        fromPercentage,
        fromFlat,
        toPercentage,
        toFlat,
        startDate,
        endDate,
      })
    } else {
      await putRequest(`/advs/v1/boosts/${advertiserUuid}`, {
        boostUuids: boosts.boostUuids,
        channelUuids,
        name: values.name,
        department: values.department,
        fromPercentage,
        fromFlat,
        toPercentage,
        toFlat,
        startDate,
        endDate,
      })
    }

    hideModal()
    onDone()
  })

  return (
    <>
      <div className="card-header">
        <div className="card-title">
          {isNew ? 'New campaign boost' : 'Edit campaign boost'}
        </div>
      </div>
      <div className="card-body">
        <form className="boost-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="row m-r--5 m-b-5">
            {/* Department */}
            <div className="control control-select flex-1-3">
              <label>Department</label>
              <select
                className="w-100"
                {...register('department', { required: true })}
              >
                {departments.map((department) => (
                  <option key={department.key} value={department.key}>
                    {department.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Boost name */}
            <div className="control flex-2-3">
              <label>Campaign boost name</label>
              <input
                type="text"
                {...register('name', {
                  required: true,
                })}
              />
            </div>
          </div>

          {/* Dates */}
          <div className="row m-r--3 m-b-5">
            <div className="control control-date flex-1-2">
              <label>Start date</label>
              <Controller
                render={({ field: { onChange, value, name, ref } }) => (
                  <DateInput
                    innerRef={ref}
                    selected={value}
                    onChange={(newDate) => {
                      onChange(newDate)
                    }}
                    highlightDates={[new Date()]}
                  />
                )}
                name="startDate"
                rules={{ required: true }}
                control={control}
                defaultValue={new Date()}
              />
            </div>
            <div className="control control-date flex-1-2">
              <label>End date</label>
              <Controller
                render={({ field: { onChange, value, name, ref } }) => (
                  <DateInput
                    innerRef={ref}
                    selected={value}
                    onChange={(newDate) => {
                      onChange(newDate)
                    }}
                    highlightDates={[new Date()]}
                  />
                )}
                name="endDate"
                control={control}
                defaultValue={new Date()}
              />
            </div>
          </div>

          {/* Commission value */}
          <div className="row m-r--5 m-b-5">
            <div className="control control-select flex-1-3">
              <label>Commission change</label>
              <select
                className="w-100"
                {...register('commissionType', { required: true })}
              >
                <option value="percentage">Percentage</option>
                <option value="flat">Flat</option>
              </select>
            </div>

            <div className="control flex-1-3">
              <label>From</label>
              <input
                type="number"
                step="0.01"
                {...register('fromValue', {
                  required: true,
                })}
              />
            </div>
            <div className="control flex-1-3">
              <label>To</label>
              <input
                type="number"
                step="0.01"
                {...register('toValue', {
                  required: true,
                })}
              />
            </div>
          </div>

          {/* Sites */}
          <div className="block m-b-5">
            <div className="control block">
              <label>Sites</label>
              <div className="column column-narrow">
                <div className="control control-radio">
                  <input
                    type="radio"
                    name="channels-method"
                    value="all"
                    checked={selectedChannelsMethod === 'all'}
                    onChange={() => setSelectedChannelsMethod('all')}
                  />
                  <label>All sites</label>
                </div>
                <div className="control control-radio">
                  <input
                    type="radio"
                    name="channels-method"
                    value="specific"
                    checked={selectedChannelsMethod === 'specific'}
                    onChange={() => setSelectedChannelsMethod('specific')}
                  />
                  <label>Specific sites</label>
                </div>
              </div>
            </div>

            <div
              className={varClass({
                'subnav-filters w-50 m-t-2': true,
                'disabled': selectedChannelsMethod !== 'specific',
              })}
            >
              <MultiSelectDropdown
                label="Sites"
                values={channels}
                sortKey="channelUuid"
                uuidGetter={(channel: IChannelsV1List) => channel.channelUuid}
                labelGetter={(channel: IChannelsV1List) =>
                  channel.channelName || channel.channelDomain
                }
                selectedValues={selectedChannels}
                toggleSelectedValue={toggleSelectedChannel}
                clearSelectedValues={clearSelectedChannels}
              />
            </div>
          </div>

          <div className="text-right m-t-5">
            <button className="btn btn-bordered m-r-2" onClick={hideModal}>
              Cancel
            </button>
            <button
              className="btn btn-dark"
              type="submit"
              disabled={submitting}
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </>
  )
}
